import { sortBy } from 'lodash-es';
import { ImResponse, leyanbotClient, omsV2Client, resolve } from 'services/api';

export function getActivityCount() {
  return resolve(
    leyanbotClient.get<
      ImResponse<{
        activiyCount: number;
        historyCount: number;
      }>
    >('/oversea-knowledge/activity_knowledge/activities/count'),
  );
}

export interface GetActivityListBody {
  page: number;
  pageSize: number;
  status?: 'HISTORY' | 'PROGRESS' | 'PLANNING' | 'UNUSED';
  storeIds: (number | string)[];
  platformStoreScope?: {
    platform: General.PlatformUpperCase;
    regionStoreScopes: {
      region: General.UpperCaseRegion;
    }[];
  }[];
  activityTypes?: Knowledge.Activity.Type[];
  statuses?: ('HISTORY' | 'PROGRESS' | 'PLANNING')[];
  activityFrom?: string;
  activityTo?: string;
}

export function getActivityList(body: GetActivityListBody) {
  return resolve(
    leyanbotClient.post<
      ImResponse<{
        total: number;
        list: Knowledge.Activity.Detail[];
      }>
    >('/oversea-knowledge/activity_knowledge/activities/list', body),
  );
}

export function getActivityDetail(id: number | string) {
  return resolve(
    leyanbotClient.get<ImResponse<Knowledge.Activity.Detail>>(
      `/oversea-knowledge/activity_knowledge/activities/${id}`,
    ),
  );
}

export function createActivity(body: Omit<Knowledge.Activity.Detail, 'id'>) {
  return resolve(
    leyanbotClient.post<ImResponse<Knowledge.Activity.Detail>>(
      '/oversea-knowledge/activity_knowledge/activities',
      body,
      {
        params: { activity_type: body.activityType },
      },
    ),
  );
}

export function updateActivity({ id, ...body }: Knowledge.Activity.Detail) {
  return leyanbotClient.put(`/oversea-knowledge/activity_knowledge/activities/${id}`, body);
}

export function deleteActivity(id: number | string) {
  return leyanbotClient.delete(`/oversea-knowledge/activity_knowledge/activities/${id}`);
}

export function getKnowledgePolicy(knowledgeId: string | number) {
  return resolve(
    omsV2Client.get<
      ImResponse<{
        knowledgeScenarioPolicyList: Knowledge.Policy.KnowledgeScenarioPolicy[];
        repurchasePeriodDays?: number;
      }>
    >('/knowledgePolicy', { params: { knowledgeId } }),
  ).then(({ knowledgeScenarioPolicyList, ...others }) => {
    return {
      ...others,
      knowledgeScenarioPolicyList: knowledgeScenarioPolicyList
        .sort((a, b) => {
          const sendPoint = [
            'EFFECTIVE',
            'EFFECTIVE_IN_QUANTUM',
            'EFFECTIVE_NOT_IN_QUANTUM',
            'WARM_UP',
          ] as const;

          const index1 = sendPoint.indexOf(a.activitySendPoint);
          const index2 = sendPoint.indexOf(b.activitySendPoint);

          if (index1 > index2) {
            return 1;
          }

          if (index1 < index2) {
            return -1;
          }

          const scenarios = [
            'WELCOME_REPLY',
            'ORDER_REMINDER',
            'PAYMENT_REMINDER',
            'CONFIRM_TRADE',
            'SIGN_REMINDER',
            'POSITIVE_REVIEW_CARE',
            'RECENT_CONSULT_NO_ORDER',
            'RECENT_ORDER_COMPLETED',
            'INITIATIVE_RECENT_CONSULT_ACTIVITY_TIME_NO_ORDER',
            'INITIATIVE_HIGH_REPURCHASE',
            'INITIATIVE_ACTIVITY_POTENTIAL',
            'INITIATIVE_HIGH_VALUE',
            'INITIATIVE_OTHER',
          ] as const;

          const scenarioIndex1 = scenarios.indexOf(a.scenarioType);
          const scenarioIndex2 = scenarios.indexOf(b.scenarioType);

          return scenarioIndex1 - scenarioIndex2;
        })
        .map(({ regionPolicyList, ...others }) => ({
          ...others,
          regionPolicyList: regionPolicyList.map(({ platformPolicyList, ...others }) => ({
            ...others,
            platformPolicyList: sortBy(platformPolicyList, 'itemHighlight').reverse(),
          })),
        })),
    };
  });
}

export function createOrUpdateKnowledgePolicy(body: {
  knowledgeId: number;
  knowledgeType: 'ACTIVITY' | 'COMMON';
  activityName: string;
  storeIds: number[];
  repurchasePeriodDays?: number;
  knowledgeScenarioPolicyList: Knowledge.Policy.KnowledgeScenarioPolicy[];
}) {
  return omsV2Client.post('knowledgePolicy', body);
}

const getLangMap = (cn: string, multiLangString: string) => {
  const multiLang = JSON.parse(multiLangString);
  return {
    'zh-CN': multiLang.CN || cn,
    'en-US': multiLang.EN,
    id: multiLang.ID,
    th: multiLang.TH,
    tl: multiLang.PH,
    vi: multiLang.VN,
    es: multiLang.ES,
    pt: multiLang.PT,
  };
};

export function getThemeList() {
  return leyanbotClient
    .get<any>('/oversea-knowledge/activity_knowledge/activities/themes')
    .then(({ data }) => {
      return data.data.map(({ id, name, multiLang, highlightList }: any) => {
        return {
          id,
          langMap: getLangMap(name, multiLang),
          highlightList: highlightList.map(({ id, content, multiLang }: any) => {
            return {
              id,
              langMap: getLangMap(content, multiLang),
            };
          }),
        } as Knowledge.Activity.Theme;
      }) as Knowledge.Activity.Theme[];
    });
}

export function getActivityTypeHighlightListMap() {
  return leyanbotClient
    .get<any>('/oversea-knowledge/activity_knowledge/activities/activity_type_template_highlights')
    .then(({ data }) => {
      return data.data.reduce((res: any, { name, highlightList }: any) => {
        res[name] = highlightList.map(({ id, content, multiLang }: any) => {
          return {
            id,
            langMap: getLangMap(content, multiLang),
          };
        });
        return res;
      }, {}) as Partial<Record<Knowledge.Activity.Type, Knowledge.Activity.RecommendHighlight[]>>;
    });
}

export function getRecommendSlogans(items: Knowledge.Activity.Item[]) {
  return resolve(
    leyanbotClient.post<ImResponse<string[]>>(
      '/oversea-knowledge/activity_knowledge/activity/recommend_item_names',
      { items },
    ),
  );
}

export function getTiktokActivities(internalstoreIdList?: string) {
  return resolve(
    leyanbotClient.get<ImResponse<Knowledge.Activity.TiktokActivity[]>>(
      '/oversea-knowledge/activity_knowledge/tiktok_official_activities',
      { params: { internalstoreIdList } },
    ),
  );
}

export function getTiktokActivityItems(platformIds: string) {
  return resolve(
    leyanbotClient.get<
      ImResponse<{
        itemList: Knowledge.Activity.Item[];
        containsDirectDiscount: boolean;
        maxDiscount: string;
      }>
    >('/oversea-knowledge/activity_knowledge/tiktok_official_activities/items', {
      params: { platformIds },
    }),
  );
}

export function getScenarioConfigs() {
  return resolve(
    omsV2Client.get<
      ImResponse<{
        scenarioConfig: Knowledge.Policy.ScenarioConfig[];
      }>
    >('/knowledgePolicy/scenarioConfig'),
  );
}

export function updateScenarioConfigs(body: { scenarioConfig: Knowledge.Policy.ScenarioConfig[] }) {
  return omsV2Client.post('/knowledgePolicy/scenarioConfig', body);
}
